import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { useToast } from '../../hooks/use-toast';
import MediaPicker from '../../components/MediaPicker';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const AdminSliderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = Boolean(id);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  
  const [formData, setFormData] = useState({
    slug: '',
    image: '',
    buttonLink: '',
    order: 0,
    isActive: true,
    textColor: 'white', // Color for header icons and category nav (white/black)
    versions: []
  });

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  useEffect(() => {
    fetchEnabledCountries();
  }, []);

  useEffect(() => {
    if (isEdit && enabledCountries.length > 0) {
      fetchSlider();
    } else if (!isEdit && enabledCountries.length > 0) {
      initializeFormForCreate();
    }
  }, [id, enabledCountries]);

  const fetchEnabledCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/localization/countries`);
      const data = await response.json();
      setEnabledCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const initializeFormForCreate = () => {
    const versions = enabledCountries.map(country => ({
      countryCode: country.code,
      language: country.language,
      heading: '',
      subheading: '',
      buttonText: '',
      isAvailable: true
    }));

    setFormData({
      slug: '',
      image: '',
      buttonLink: '',
      order: 0,
      isActive: true,
      textColor: 'white',
      versions: versions
    });
  };

  const fetchSlider = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/sliders/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const slider = data.find(s => (s._id || s.id) === id);
      
      if (slider) {
        // Merge existing versions with newly enabled countries
        const existingVersions = slider.versions || [];
        const mergedVersions = enabledCountries.map(country => {
          const existingVersion = existingVersions.find(v => v.countryCode === country.code);
          
          if (existingVersion) {
            return existingVersion;
          } else {
            return {
              countryCode: country.code,
              language: country.language,
              title: '',
              subtitle: '',
              buttonText: '',
              buttonLink: '',
              image: '',
              isAvailable: true
            };
          }
        });
        
        setFormData({
          slug: slider.slug || '',
          image: slider.image || '',
          buttonLink: slider.buttonLink || '',
          order: slider.order || 0,
          isActive: slider.isActive !== undefined ? slider.isActive : true,
          textColor: slider.textColor || 'white',
          versions: mergedVersions
        });
      } else {
        toast({
          title: 'Error',
          description: 'Slider not found',
          variant: 'destructive'
        });
        navigate('/admin/sliders');
      }
    } catch (error) {
      console.error('Error fetching slider:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch slider',
        variant: 'destructive'
      });
      navigate('/admin/sliders');
    }
  };

  const updateVersionField = (versionIndex, field, value) => {
    const newVersions = [...formData.versions];
    newVersions[versionIndex] = {
      ...newVersions[versionIndex],
      [field]: value
    };
    setFormData({ ...formData, versions: newVersions });
  };

  const handleSave = async () => {
    try {
      if (!formData.slug) {
        toast({
          title: 'Validation Error',
          description: 'Slug is required',
          variant: 'destructive',
        });
        return;
      }

      if (!formData.image) {
        toast({
          title: 'Validation Error',
          description: 'Slider image is required',
          variant: 'destructive',
        });
        return;
      }

      const hasValidVersion = formData.versions.some(v => v.heading && v.heading.trim() !== '');
      if (!hasValidVersion) {
        toast({
          title: 'Validation Error',
          description: 'At least one country version with a heading is required',
          variant: 'destructive',
        });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('accessToken');
      let response;

      if (isEdit) {
        response = await fetch(`${API_URL}/api/sliders/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${API_URL}/api/sliders`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save slider');
      }

      toast({
        title: 'Success',
        description: isEdit ? 'Slider updated successfully' : 'Slider created successfully',
      });

      navigate('/admin/sliders');
    } catch (error) {
      console.error('Error saving slider:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save slider',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setFormData({ ...formData, image: imageUrl });
    setShowMediaPicker(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/admin/sliders')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Slider' : 'Add Slider'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit ? 'Update slider details and country versions' : 'Create a new slider with country-specific versions'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Master Slider Information */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4">Master Slider Information</h3>
          
          <div className="space-y-4">
            {/* Slug */}
            <div>
              <Label>Slug (Identifier) *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="slider-name"
              />
            </div>

            {/* Slider Image */}
            <div>
              <Label>Slider Image *</Label>
              <div className="mt-2 space-y-2">
                {formData.image && (
                  <div className="relative inline-block w-full max-w-2xl">
                    <img
                      src={resolveMediaUrl(formData.image)}
                      alt="Slider"
                      className="w-full h-48 object-cover rounded border"
                      onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x192?text=No+image'; }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({ ...formData, image: '' })}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMediaPicker(true)}
                  className="w-full"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {formData.image ? 'Change Image' : 'Select Image'}
                </Button>
              </div>
            </div>

            {/* Button Link */}
            <div>
              <Label>Button Link (URL)</Label>
              <Input
                value={formData.buttonLink}
                onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                placeholder="/shop or https://example.com"
              />
              <p className="text-xs text-gray-500 mt-1">Where the button should link to</p>
            </div>

            {/* Order */}
            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label className="font-normal cursor-pointer">Active Slider</Label>
            </div>

            {/* Text Color Configuration */}
            <div>
              <Label>Header & Navigation Text Color</Label>
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-500">
                  Choose the color for header icons and category navigation when this slider is active
                </p>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, textColor: 'white' })}
                    className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                      formData.textColor === 'white'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="text-white text-xl">Aa</span>
                      </div>
                      <span className="font-medium">White Text</span>
                      <span className="text-xs text-gray-500">For dark backgrounds</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, textColor: 'black' })}
                    className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                      formData.textColor === 'black'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center border">
                        <span className="text-gray-900 text-xl">Aa</span>
                      </div>
                      <span className="font-medium">Black Text</span>
                      <span className="text-xs text-gray-500">For light backgrounds</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Country Versions Accordion */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Country Versions</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create text versions for each country. At least one version with a heading is required.
          </p>

          <Accordion type="multiple" value={expandedAccordions} onValueChange={setExpandedAccordions}>
            {formData.versions.map((version, versionIndex) => {
              const country = enabledCountries.find(c => c.code === version.countryCode);
              if (!country) return null;

              return (
                <AccordionItem key={versionIndex} value={`country-${versionIndex}`} className="border rounded-lg mb-2">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                      <div className="text-left">
                        <div className="font-semibold">{country.name}</div>
                        <div className="text-sm text-gray-500">
                          {country.languageName}
                          {version.heading && <span className="ml-2 text-blue-600">• {version.heading}</span>}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4 pt-4">
                      {/* Heading */}
                      <div>
                        <Label>Heading ({country.languageName}) *</Label>
                        <Input
                          value={version.heading}
                          onChange={(e) => {
                            updateVersionField(versionIndex, 'heading', e.target.value);
                            if (!isEdit && versionIndex === 0 && !formData.slug) {
                              setFormData({ ...formData, slug: generateSlug(e.target.value) });
                            }
                          }}
                          placeholder={`Main heading in ${country.languageName}`}
                        />
                      </div>

                      {/* Subheading */}
                      <div>
                        <Label>Subheading ({country.languageName})</Label>
                        <Textarea
                          value={version.subheading}
                          onChange={(e) => updateVersionField(versionIndex, 'subheading', e.target.value)}
                          rows={2}
                          placeholder="Secondary text or description"
                        />
                      </div>

                      {/* Button Text */}
                      <div>
                        <Label>Button Text ({country.languageName})</Label>
                        <Input
                          value={version.buttonText}
                          onChange={(e) => updateVersionField(versionIndex, 'buttonText', e.target.value)}
                          placeholder={`e.g., "Shop Now" in ${country.languageName}`}
                        />
                      </div>

                      {/* Availability */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={version.isAvailable}
                          onCheckedChange={(checked) => updateVersionField(versionIndex, 'isAvailable', checked)}
                        />
                        <Label className="font-normal cursor-pointer">
                          ✓ Available in {country.name}
                        </Label>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/sliders')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#be1f1a] hover:bg-[#a71b17]"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Slider' : 'Create Slider')}
          </Button>
        </div>
      </div>

      {/* Media Picker */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleImageSelect}
        fileType="image"
      />
    </div>
  );
};

export default AdminSliderForm;
